import {
  Button,
  Center,
  Container,
  Heading,
  Icon,
  Kbd,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { LucideHouse, LucideRedo } from "lucide-react";
import { useRouter } from "next/router";
import { useRef } from "react";
import type { FallbackProps } from "react-error-boundary";

import { useKeyPress } from "~/hooks/useKeyPress";
import {
  NotFoundError,
  RedirectError,
  UnauthenticatedError,
  UnauthorizedError,
} from "~/lib/error";

function getErrorInfo(error: Error) {
  if (error instanceof UnauthorizedError) {
    return {
      title: "Nothing to see here...",
      description:
        "You don't have the right permissions to access this page. If you think this is a mistake, please contact us.",
    };
  } else if (error instanceof NotFoundError) {
    return {
      title: "The page you're looking for doesn't exist",
      description:
        "Please check the URL and try again. It's also possible that the page has been moved or deleted.",
    };
  } else if (error instanceof UnauthenticatedError) {
    return {
      title: "Your session has expired",
      description:
        "Please try to reload the page or sign in again. Your session will be automatically refreshed in the future.",
    };
  } else {
    return {
      title: "Something went wrong",
      description: error.message,
    };
  }
}

function Loading() {
  return (
    <Center display="fixed" inset={0} w="100vw" h="100vh" bg="blackAlpha.800">
      <Spinner />
    </Center>
  );
}

export function RootErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const router = useRouter();
  const toast = useToast();
  const { title, description } = getErrorInfo(error);

  function onGoHomeClick() {
    void router.push("/");
  }

  function onReloadClick() {
    window.location.reload();
  }

  useKeyPress("1", onGoHomeClick);
  useKeyPress("2", onReloadClick);

  if (error instanceof RedirectError) {
    window.location.assign(error.path);
    return <Loading />;
  }

  return (
    <Container maxW="container.sm" py={16}>
      <Stack spacing={12}>
        <VStack textAlign="center">
          <Text as="span" textTransform="uppercase" fontWeight="semibold">
            Error
          </Text>
          <Heading as="h1" size={{ base: "xs", md: "md" }}>
            {title}
          </Heading>
          <Text fontSize="lg" color="subtle">
            {description}
          </Text>
        </VStack>

        <Stack>
          <Heading as="h2" fontSize="sm">
            Where to go from here:
          </Heading>

          <Button
            leftIcon={<Icon as={LucideHouse} boxSize={4} />}
            iconSpacing={5}
            width="full"
            h="auto"
            py={3}
            px={5}
            variant="solid"
            onClick={onGoHomeClick}>
            <Stack flex="1" textAlign="left" spacing={0}>
              <Text fontSize="sm">Go home</Text>
            </Stack>
            <Kbd>1</Kbd>
          </Button>

          <Button
            leftIcon={<Icon as={LucideRedo} boxSize={4} />}
            iconSpacing={5}
            width="full"
            h="auto"
            py={3}
            px={5}
            variant="solid"
            onClick={onReloadClick}>
            <Stack flex="1" textAlign="left" spacing={0}>
              <Text fontSize="sm">Reload the page</Text>
            </Stack>
            <Kbd>2</Kbd>
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
